import type { CampaignPreviewResponse } from '@carbonwebmail/contracts'
import { useState } from 'react'
import type { PreviewDevice } from './MessageFrame.js'
import { MessageFrame, PREVIEW_DEVICES } from './MessageFrame.js'

/**
 * The message as it would arrive.
 *
 * The frame is `MessageFrame`, shared with the full-tab preview, so the one
 * decision about what a previewed message is allowed to do is made once.
 *
 * The device panes differ only in width, because that is genuinely the
 * difference — the markup is one table layout and a narrow viewport is what a
 * phone does to it. The plain-text pane is not a courtesy: it is what a screen
 * reader, a plain-text client and a spam filter all read, and it is the half
 * nobody checks until it is wrong.
 *
 * Shared by the composer and the template editor. One copy, so a fix to the
 * frame or a pane reaches both.
 */

type Pane = PreviewDevice | 'text'

const PANES: readonly (readonly [Pane, string])[] = [...PREVIEW_DEVICES, ['text', 'Plain text']]

export function MessagePreview({
  preview,
}: {
  readonly preview: CampaignPreviewResponse | undefined
}) {
  const [pane, setPane] = useState<Pane>('desktop')

  return (
    <>
      <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
        <h2 className="font-bold text-[15px] text-t1">Preview</h2>
        <div className="flex gap-1.5">
          {PANES.map(([value, label]) => (
            <button
              key={value}
              type="button"
              onClick={() => setPane(value)}
              aria-pressed={pane === value}
              className={`rounded-pill px-3 py-1.5 font-semibold text-[12px] ${
                pane === value
                  ? 'bg-brand-soft text-brand-strong'
                  : 'text-t2 hover:bg-line-2 hover:text-t1'
              }`}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {preview === undefined ? (
        <p className="rounded-field border border-line border-dashed px-3 py-10 text-center text-[13px] text-t3">
          Write a subject and a message to see how it will look.
        </p>
      ) : (
        <>
          <p className="mb-3 truncate border-line border-b pb-3 text-[13px] text-t2">
            <span className="font-semibold text-t1">Subject</span> {preview.subject}
          </p>
          {pane === 'text' ? (
            <pre className="h-[560px] overflow-auto whitespace-pre-wrap rounded-field bg-surface-2 p-4 text-[12px] text-t1 leading-relaxed">
              {preview.text}
            </pre>
          ) : (
            <MessageFrame className="h-[560px] rounded-field" device={pane} html={preview.html} />
          )}
        </>
      )}
    </>
  )
}
